/**
 * counter.ts
 * Counterノードの定義
 * 数値を一定間隔で進めながら後続ノードを反復実行するためのノード
 */
import type { NodeDefinition } from '../../types';
import { t, r } from '../../rubyParts';
import { COUNTER_SOURCE_HANDLES, COUNTER_TARGET_HANDLES } from '../counterHandles';
import { CounterNodeView } from '../views/CounterNode';
import { CounterSettings } from '../settings/CounterSettings';

const MAX_COUNTER_ITERATIONS = 1000;

const roundCounterValue = (value: number): number => Number.parseFloat(value.toFixed(8));

const isCounterValueInRange = (current: number, step: number, final: number): boolean => (step > 0 ? current <= final : current >= final);

/**
 * Counterノードの定義
 * - パレットに表示してユーザーがドラッグ配置できる
 * - 実行時に条件内/完了の出力ハンドルを返す
 */
export const counterDefinition: NodeDefinition<'counter'> = {
  label: 'Counter',
  showInPalette: true,
  paletteColor: 'bg-amber-100 border-amber-500 text-amber-700',
  miniMapColor: '#f59e0b',
  defaultData: {
    label: 'Counter',
    initialValue: 0,
    stepValue: 1,
    finalValue: 5,
  },
  view: CounterNodeView,
  settings: CounterSettings,
  loopEntryHandle: COUNTER_TARGET_HANDLES.repeat,
  run: (node, ctx, log, meta) => {
    const { initialValue, stepValue, finalValue } = node.data;

    if (![initialValue, stepValue, finalValue].every((value) => Number.isFinite(value))) {
      log('[Counter] エラー: はじめの数・ふえる数・おわりの数は数字で入力してください');
      return { nextHandle: null };
    }

    if (Math.abs(stepValue) < Number.EPSILON) {
      log('[Counter] エラー: ふえる数は 0 以外にしてください');
      return { nextHandle: null };
    }

    const counterStates = ctx.counterStates ?? (ctx.counterStates = {});
    const isRepeatInput = meta?.incomingHandle === COUNTER_TARGET_HANDLES.repeat;

    if (!isRepeatInput) {
      counterStates[node.id] = { current: initialValue, iteration: 0 };
      log(`[Counter] くりかえし開始 (${initialValue} -> ${finalValue}, step ${stepValue})`);
    } else {
      const previous = counterStates[node.id];
      if (!previous) {
        counterStates[node.id] = { current: initialValue, iteration: 0 };
        log('[Counter] 注意: 「もどる」に先に到達したため、はじめの数から開始します');
      } else {
        counterStates[node.id] = {
          current: roundCounterValue(previous.current + stepValue),
          iteration: previous.iteration + 1,
        };
      }
    }

    const state = counterStates[node.id];
    if (state.iteration > MAX_COUNTER_ITERATIONS) {
      log(`[Counter] エラー: くりかえし回数が多すぎます（上限 ${MAX_COUNTER_ITERATIONS} 回）`);
      return { nextHandle: null };
    }

    if (isCounterValueInRange(state.current, stepValue, finalValue)) {
      ctx.lastValue = state.current;
      log(`[Counter] 値: ${state.current}`);
      return { nextHandle: COUNTER_SOURCE_HANDLES.inRange, branchLabel: 'くりかえす' };
    }

    log(`[Counter] おわり: ${state.current}`);
    return { nextHandle: COUNTER_SOURCE_HANDLES.done, branchLabel: 'おわり' };
  },
  help: {
    title: [t('Counter（'), r('回数', 'かいすう'), t('でくりかえす）')],
    description: [
      r('同', 'おな'),
      t('じ'),
      r('道', 'みち'),
      t('を'),
      r('何回', 'なんかい'),
      t('もくりかえすためのカードです。「くりかえす」から'),
      r('出', 'で'),
      t('た'),
      r('道', 'みち'),
      t('を、「もどる」につなぎます。'),
      r('数', 'かず'),
      t('は「はじめの'),
      r('数', 'かず'),
      t('」から'),
      r('始', 'はじ'),
      t('まり、もどってくるたびに「ふえる'),
      r('数', 'かず'),
      t('」ずつふえます。「おわりの'),
      r('数', 'かず'),
      t('」をこえたら「おわり」へ'),
      r('進', 'すす'),
      t('みます。'),
    ],
  },
};
